/**
 * Dataset dos casos de garantia/RMA (Task 15, brief 3.6).
 *
 * Uma fonte (`/api/rma/cases`) com o seu estado declarado, no mesmo vocabulário
 * das demais telas (`live` vs `awaiting_contract`): fonte ausente é dita, nunca
 * convertida em "nenhum caso".
 *
 * Os seletores aqui são a fonte ÚNICA de "quais casos compõem este recorte":
 * a fila e o detalhe leem daqui, então não existe contagem de tela discordando
 * de outra. Puro e sem I/O.
 */
import {
  RMA_STATES,
  RMA_STATE_LABELS,
  nextRmaStates,
  rmaRequirementsFor,
  isTerminalRmaState,
  type RmaState,
  type RmaTransitionField
} from "./state-machine.js";
import type { DashboardSourceState } from "../dashboard/kpis.js";
import type { PlatformRmaCase, PlatformRmaEvent } from "../api/types.js";

export interface RmaSources {
  rmaCases: DashboardSourceState;
}

export interface RmaDataset {
  cases: PlatformRmaCase[];
  sources: RmaSources;
}

/** Estado inicial honesto: nada buscado ainda (não é "nenhum caso"). */
export function emptyRmaDataset(): RmaDataset {
  return { cases: [], sources: { rmaCases: "awaiting_contract" } };
}

export function isRmaSourceAwaitingContract(dataset: RmaDataset): boolean {
  return dataset.sources.rmaCases === "awaiting_contract";
}

export function rmaCaseById(dataset: RmaDataset, caseId: string): PlatformRmaCase | null {
  return dataset.cases.find((rmaCase) => rmaCase.id === caseId) ?? null;
}

export function casesByState(dataset: RmaDataset, state: RmaState): PlatformRmaCase[] {
  return dataset.cases.filter((rmaCase) => rmaCase.state === state);
}

/** Casos ainda não encerrados — a fila que compõe o KPI "garantias abertas". */
export function openCases(dataset: RmaDataset): PlatformRmaCase[] {
  return dataset.cases.filter((rmaCase) => rmaCase.state !== "closed");
}

export interface StateBreakdownRow {
  state: RmaState;
  label: string;
  count: number;
}

/** Composição da fila por etapa, em ordem estável. */
export function stateBreakdown(dataset: RmaDataset): StateBreakdownRow[] {
  return RMA_STATES.map((state) => ({
    state,
    label: RMA_STATE_LABELS[state],
    count: casesByState(dataset, state).length
  }));
}

/**
 * Linha do tempo do caso: do passo mais antigo ao mais recente.
 *
 * Ordenação estável por `occurredAt` e, em empate, pela ordem em que o servidor
 * publicou — dois passos no mesmo milissegundo não podem trocar de lugar entre
 * renderizações.
 */
export function rmaTimeline(rmaCase: PlatformRmaCase): PlatformRmaEvent[] {
  return rmaCase.events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => a.event.occurredAt.localeCompare(b.event.occurredAt) || a.index - b.index)
    .map((entry) => entry.event);
}

export function lastRmaEvent(rmaCase: PlatformRmaCase): PlatformRmaEvent | null {
  return rmaTimeline(rmaCase).at(-1) ?? null;
}

export interface AvailableRmaTransition {
  to: RmaState;
  requires: readonly RmaTransitionField[];
}

/**
 * Próximas etapas que a tela pode oferecer, com o que cada uma exige. Vem da
 * máquina pura — a tela não mantém a sua própria lista de botões.
 */
export function availableRmaTransitions(rmaCase: PlatformRmaCase): AvailableRmaTransition[] {
  if (isTerminalRmaState(rmaCase.state)) return [];
  return nextRmaStates(rmaCase.state).map((to) => ({ to, requires: rmaRequirementsFor(to) }));
}

export interface OrderDrillDown {
  route: string;
  label: string;
  reference: string;
}

/**
 * Para onde navegar para ver o PEDIDO que originou o caso (D-03 aplicado ao
 * brief 3.6). `null` quando o caso não tem pedido vinculado (aberto por
 * atendimento sem o pedido localizado ainda).
 */
export function orderDrillDown(rmaCase: PlatformRmaCase): OrderDrillDown | null {
  if (!rmaCase.platformOrderId) return null;
  return {
    route: `/pedidos/${encodeURIComponent(rmaCase.platformOrderId)}`,
    label: "Abrir o pedido que originou o caso",
    reference: rmaCase.orderExternalId ?? rmaCase.platformOrderId
  };
}
